import { useEffect, useState } from "react";
import {
    listExposureActuals,
    createExposureActual,
    deleteExposureActual,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasFeature } from "@/lib/tier";
import LockedFeatureCard from "@/components/investor/LockedFeatureCard";
import { Plus, Trash2, Calculator } from "lucide-react";
import { toast } from "sonner";

const EMPTY_DRAFT = {
    category: "",
    zip_code: "",
    estimated_low: "",
    estimated_high: "",
    actual_quote_amount: "",
    contractor_name: "",
    notes: "",
};

function bandFor(low, high, actual) {
    if (low == null || high == null) return { label: "No range", tone: "text-bl-muted" };
    if (actual < low) return { label: "Below range", tone: "text-emerald-700" };
    if (actual > high) return { label: "Above range", tone: "text-[#C85B3A]" };
    return { label: "Within range", tone: "text-bl-primary" };
}

/**
 * ExposureActualsPanel — Pro+ workflow for logging actual contractor quotes
 * against the AI-estimated repair exposure ranges. Builds a transparent
 * calibration ledger over time.
 *
 * Cost-Safe: never invokes AI. Pure DB CRUD + simple comparison math.
 */
export default function ExposureActualsPanel() {
    const { user } = useAuth();
    const canAccess = hasFeature(user, "repair_exposure_calibration");
    const [data, setData] = useState({ items: [], summary: null });
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!canAccess) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const r = await listExposureActuals();
                if (!cancelled) setData(r);
            } catch (e) {
                if (!cancelled) {
                    toast.error(e?.response?.data?.detail?.message || "Could not load actuals.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [canAccess]);

    if (!canAccess) {
        return (
            <div className="max-w-2xl">
                <LockedFeatureCard
                    feature="repair_exposure_calibration"
                    title="Repair exposure calibration"
                    description="Compare actual contractor quotes against estimated ranges over time — the calibration ledger that makes the engine smarter on your terms. Upgrade to Pro to unlock."
                />
            </div>
        );
    }

    const onCreate = async (e) => {
        e.preventDefault();
        const amt = Number(draft.actual_quote_amount);
        if (!amt || amt <= 0) {
            toast.error("Enter the actual quote amount.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                category: draft.category || null,
                zip_code: draft.zip_code || null,
                estimated_low: draft.estimated_low === "" ? null : Number(draft.estimated_low),
                estimated_high: draft.estimated_high === "" ? null : Number(draft.estimated_high),
                actual_quote_amount: amt,
                contractor_name: draft.contractor_name || null,
                notes: draft.notes || null,
            };
            const created = await createExposureActual(payload);
            setData((d) => {
                const items = [created, ...d.items];
                const summary = recomputeSummary(items);
                return { ...d, items, summary };
            });
            setDraft(EMPTY_DRAFT);
            toast.success("Actual quote logged.");
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not log quote.");
        } finally {
            setSubmitting(false);
        }
    };

    const onDelete = async (aid) => {
        if (!confirm("Delete this calibration entry?")) return;
        try {
            await deleteExposureActual(aid);
            setData((d) => {
                const items = d.items.filter((i) => i.actual_id !== aid);
                return { ...d, items, summary: recomputeSummary(items) };
            });
        } catch (e) {
            toast.error(e?.response?.data?.detail?.message || "Could not delete.");
        }
    };

    const s = data.summary || { total: 0, within: 0, below: 0, above: 0, no_range: 0 };

    return (
        <div className="space-y-5" data-testid="exposure-actuals-panel">
            {/* Summary */}
            <div className="bl-card p-5 md:p-6 grid grid-cols-2 md:grid-cols-5 gap-3">
                {[
                    { label: "Total", value: s.total, tone: "text-bl" },
                    { label: "Within range", value: s.within, tone: "text-bl-primary" },
                    { label: "Below range", value: s.below, tone: "text-emerald-700" },
                    { label: "Above range", value: s.above, tone: "text-[#C85B3A]" },
                    { label: "No range", value: s.no_range, tone: "text-bl-muted" },
                ].map((k) => (
                    <div key={k.label} className="text-center">
                        <div className={`font-display font-semibold text-2xl ${k.tone}`}>
                            {k.value}
                        </div>
                        <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                            {k.label}
                        </div>
                    </div>
                ))}
            </div>

            {/* Add actual form */}
            <form
                onSubmit={onCreate}
                className="bl-card p-4 md:p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
                data-testid="ea-add-form"
            >
                <input
                    type="text"
                    placeholder="Category"
                    value={draft.category}
                    onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    data-testid="ea-input-category"
                />
                <input
                    type="text"
                    placeholder="ZIP"
                    value={draft.zip_code}
                    onChange={(e) => setDraft({ ...draft, zip_code: e.target.value })}
                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    maxLength={10}
                    data-testid="ea-input-zip"
                />
                <input
                    type="number"
                    placeholder="Est. low $"
                    min="0"
                    value={draft.estimated_low}
                    onChange={(e) => setDraft({ ...draft, estimated_low: e.target.value })}
                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    data-testid="ea-input-est-low"
                />
                <input
                    type="number"
                    placeholder="Est. high $"
                    min="0"
                    value={draft.estimated_high}
                    onChange={(e) => setDraft({ ...draft, estimated_high: e.target.value })}
                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    data-testid="ea-input-est-high"
                />
                <input
                    type="number"
                    placeholder="Actual quote $ *"
                    min="0"
                    required
                    value={draft.actual_quote_amount}
                    onChange={(e) =>
                        setDraft({ ...draft, actual_quote_amount: e.target.value })
                    }
                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm"
                    data-testid="ea-input-actual"
                />
                <input
                    type="text"
                    placeholder="Contractor (optional)"
                    value={draft.contractor_name}
                    onChange={(e) =>
                        setDraft({ ...draft, contractor_name: e.target.value })
                    }
                    className="px-3 py-2 rounded-md border border-bl bg-bl-surface text-bl text-sm sm:col-span-2"
                    data-testid="ea-input-contractor"
                />
                <button
                    type="submit"
                    disabled={submitting}
                    className="bl-btn-primary text-sm"
                    data-testid="ea-add-btn"
                >
                    <Plus className="w-4 h-4" /> Log actual
                </button>
            </form>

            {loading ? (
                <div className="text-sm text-bl-muted">Loading…</div>
            ) : data.items.length === 0 ? (
                <div className="bl-card p-8 text-center" data-testid="ea-empty">
                    <Calculator className="w-6 h-6 text-bl-muted mx-auto" />
                    <p className="mt-3 text-sm text-bl-muted">
                        No actual quotes logged yet. Each entry helps you calibrate
                        the engine to your real market.
                    </p>
                </div>
            ) : (
                <div className="bl-card overflow-x-auto" data-testid="ea-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left border-b border-bl">
                                <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">Category</th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">ZIP</th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">Estimated</th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">Actual</th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">vs Range</th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-bl-muted font-medium">Contractor</th>
                                <th className="px-4 py-3" />
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((row) => {
                                const b = bandFor(
                                    row.estimated_low,
                                    row.estimated_high,
                                    row.actual_quote_amount,
                                );
                                return (
                                    <tr
                                        key={row.actual_id}
                                        className="border-b border-bl last:border-0 hover:bg-bl-bg/40"
                                        data-testid={`ea-row-${row.actual_id}`}
                                    >
                                        <td className="px-4 py-3">{row.category || "—"}</td>
                                        <td className="px-4 py-3 font-mono-bl text-xs">
                                            {row.zip_code || "—"}
                                        </td>
                                        <td className="px-4 py-3 font-mono-bl text-xs">
                                            {row.estimated_low != null && row.estimated_high != null
                                                ? `$${Number(row.estimated_low).toLocaleString()} – $${Number(row.estimated_high).toLocaleString()}`
                                                : "—"}
                                        </td>
                                        <td className="px-4 py-3 font-mono-bl text-xs">
                                            ${Number(row.actual_quote_amount).toLocaleString()}
                                        </td>
                                        <td className={`px-4 py-3 text-xs font-medium ${b.tone}`}>
                                            {b.label}
                                        </td>
                                        <td className="px-4 py-3 text-bl">
                                            {row.contractor_name || "—"}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <button
                                                onClick={() => onDelete(row.actual_id)}
                                                className="text-bl-muted p-1.5 rounded hover:bg-[#C85B3A]/10 hover:text-[#C85B3A]"
                                                aria-label="Delete entry"
                                                data-testid={`ea-delete-${row.actual_id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <p className="text-xs text-bl-muted leading-relaxed">
                These actuals are private to your account and shape your view —
                they don't feed back into the public estimator yet.
            </p>
        </div>
    );
}

function recomputeSummary(items) {
    const s = { total: items.length, within: 0, below: 0, above: 0, no_range: 0 };
    for (const r of items) {
        if (r.estimated_low == null || r.estimated_high == null) {
            s.no_range++;
        } else if (r.actual_quote_amount < r.estimated_low) {
            s.below++;
        } else if (r.actual_quote_amount > r.estimated_high) {
            s.above++;
        } else {
            s.within++;
        }
    }
    return s;
}
